/* --------------------------------------------------
                Imports
-------------------------------------------------- */
import { Controller } from "@hotwired/stimulus"
import { lockBody, unlockBody } from "../shared/body_lock.js"

/* --------------------------------------------------
                Stimulus Modal Controller
-------------------------------------------------- */
// Owns open/close for all shared modals (Turbo Frame shells via stimulus_frame_id,
// and static already-in-DOM shells). Outside triggers use "modal-trigger".
// Deep links use ?open_modal=<id> (see openFromQueryParam).
export default class extends Controller {
  static targets = [ "background", "card" ]
  static values = { frameId: String }

  connect() {
    if (!!this.data.get("param") && !!this.data.get("paramId")) {
      const newLocation = `${window.location.pathname}?${this.data.get("param")}=${this.data.get("paramId")}`
      history.pushState({ urlPath: newLocation }, "", newLocation)
    }

    this.openFromQueryParam()
  }

  get modalId() {
    return this.element.id?.replace(/^modal-/, "") || ""
  }

  // Deep links like ?open_modal=pages-lead-job-new (mailers, post-publish redirect).
  // Replaces the legacy modal.js auto-open so Stimulus shells work without data-js hooks.
  openFromQueryParam() {
    const params = new URLSearchParams(window.location.search)
    const modalId = params.get("open_modal")
    if (!modalId || modalId !== this.modalId) return

    this.open()
    params.delete("open_modal")
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname
    history.replaceState(null, "", newUrl)
  }

  show(event) {
    // If triggered by an event, ensure it originated from within this modal
    // This prevents the modal from opening when Turbo preloads other frames
    if (event && event.target && !this.element.contains(event.target)) {
      return
    }

    this.open()
  }

  showFromFrame(event) {
    if (!this.hasFrameIdValue || event.target.id !== this.frameIdValue) return
    if (!event.target.innerHTML.trim()) return

    this.open()
  }

  closeOnSubmitEnd(event) {
    if (!this.element.contains(event.target)) return
    if (!event.detail.success) return

    this.close()
  }

  open() {
    this.previouslyFocused = document.activeElement
    this.element.classList.remove("hidden")
    this.element.setAttribute("aria-hidden", "false")
    lockBody()
    if (this.hasCardTarget) this.cardTarget.focus()
    this.element.dispatchEvent(new CustomEvent("modal:opened", { bubbles: true, detail: { id: this.modalId } }))
  }

  close() {
    this.element.classList.add("hidden")
    this.element.setAttribute("aria-hidden", "true")
    unlockBody()
    this.previouslyFocused?.focus?.()
    this.previouslyFocused = null
    this.element.dispatchEvent(new CustomEvent("modal:closed", { bubbles: true, detail: { id: this.modalId } }))
  }

  closeBackground(event) {
    if (event.target === this.backgroundTarget) {
      this.close()
    }
  }

  closeScroll(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  handleKeyup(e) {
    if (e.code === "Escape" && !this.element.classList.contains("hidden")) {
      this.close()
    }
  }
}
